import { useParams, Navigate, Link } from "react-router-dom";
import { Trophy } from "lucide-react";
import { Layout } from "../components/Layout";
import { Seo } from "../components/Seo";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { Reveal, MaskedLines } from "../components/motion";
import { AffiliateButton, ScoreBadge } from "../components/Affiliate";
import { VpnLogo } from "../components/VpnLogo";
import { ComparisonTable } from "../components/ComparisonTable";
import { RelatedGuides } from "../components/RelatedGuides";
import { CtaBanner } from "../components/CtaBanner";
import { getVpn } from "../data/vpns";
import { getComparison, COMPARISONS } from "../data/comparisons";

const ComparePage = () => {
  const { slug } = useParams();
  const cmp = getComparison(slug);
  if (!cmp) return <Navigate to="/compare" replace />;
  const a = getVpn(cmp.a);
  const b = getVpn(cmp.b);
  const winner = getVpn(cmp.winner);

  return (
    <Layout>
      <Seo
        title={`${a.name} vs ${b.name} (2026): Full Comparison & Winner`}
        description={`${a.name} or ${b.name}? We tested both on speed, streaming, privacy and price. ${cmp.verdict.slice(0, 120)}`}
      />
      <section className="grain relative overflow-hidden bg-[#0F172A]">
        <div className="hero-grid absolute inset-0" />
        <div className="relative mx-auto max-w-7xl px-6 py-20 lg:px-8">
          <p className="mb-4 text-xs font-bold uppercase tracking-[0.25em] text-[#7EAAFF]">Head-to-Head Comparison</p>
          <h1 className="font-display text-4xl font-black tracking-tighter text-white sm:text-5xl lg:text-6xl">
            <MaskedLines lines={[`${a.name} vs ${b.name}`, "Which Wins in 2026?"]} />
          </h1>
          <div className="mt-10 grid max-w-2xl grid-cols-1 gap-px border border-white/10 bg-white/10 sm:grid-cols-2">
            {[a, b].map((v) => (
              <div key={v.slug} className="flex items-center gap-4 bg-[#0F172A] p-6">
                <VpnLogo vpn={v} size={56} tile />
                <ScoreBadge score={v.score} dark={v.slug !== cmp.winner} />
                <div>
                  <p className="font-display text-lg font-extrabold text-white">{v.name}</p>
                  <p className="text-xs text-slate-400">#{v.rank} overall · from {v.price}/mo</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Breadcrumbs crumbs={[{ label: "Compare", to: "/compare" }, { label: `${a.name} vs ${b.name}` }]} />

      <div className="mx-auto max-w-7xl px-6 py-16 lg:px-8">
        <Reveal>
          <div className="border-2 border-[#4687FF] bg-[#4687FF]/[0.04] p-8" data-testid="compare-verdict-box">
            <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#4687FF]">
              <Trophy size={14} /> Winner: {winner.name}
            </p>
            <p className="mt-3 max-w-3xl text-base leading-relaxed text-slate-700">{cmp.verdict}</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <AffiliateButton href={winner.link} testId={`compare-winner-cta-${winner.slug}`}>
                Get {winner.name} — Winner
              </AffiliateButton>
              <AffiliateButton
                href={winner.slug === a.slug ? b.link : a.link}
                variant="outline"
                testId={`compare-runnerup-cta`}
              >
                Get {winner.slug === a.slug ? b.name : a.name}
              </AffiliateButton>
            </div>
          </div>
        </Reveal>

        <Reveal className="mt-16">
          <h2 className="mb-8 font-display text-3xl font-black tracking-tighter text-[#0F172A]">
            {a.name} vs {b.name}: Feature by Feature
          </h2>
          <div className="border border-slate-200 bg-white p-6 sm:p-8">
            <ComparisonTable vpns={[a, b]} title="Feature" />
          </div>
        </Reveal>

        <div className="mt-16 grid gap-10 lg:grid-cols-2">
          {[a, b].map((v) => (
            <Reveal key={v.slug}>
              <div className="h-full border border-slate-200 p-8">
                <div className="flex items-center gap-4">
                  <VpnLogo vpn={v} size={56} />
                  <h3 className="font-display text-xl font-extrabold tracking-tight text-[#0F172A]">
                    {v.name} in Brief — {v.score}/10
                  </h3>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">{v.miniReview}</p>
                <div className="mt-5 flex flex-wrap items-center gap-4">
                  <AffiliateButton
                    href={v.link}
                    variant={v.slug === cmp.winner ? "primary" : "dark"}
                    testId={`compare-brief-cta-${v.slug}`}
                    className="!px-5 !py-2.5 !text-xs"
                  >
                    {v.cta}
                  </AffiliateButton>
                  <Link to={`/reviews/${v.slug}`} className="text-xs font-semibold text-[#4687FF] underline underline-offset-4">
                    Full review →
                  </Link>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-16 border-t border-slate-200 pt-10">
          <h3 className="font-display text-sm font-bold uppercase tracking-[0.2em] text-slate-400">More Comparisons</h3>
          <div className="mt-4 flex flex-wrap gap-3">
            {COMPARISONS.filter((c) => c.slug !== slug).map((c) => (
              <Link
                key={c.slug}
                to={`/compare/${c.slug}`}
                className="border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition-colors duration-150 hover:border-[#4687FF] hover:text-[#4687FF]"
              >
                {getVpn(c.a).name} vs {getVpn(c.b).name}
              </Link>
            ))}
          </div>
        </Reveal>

        <RelatedGuides vpn={winner} />
      </div>
      <CtaBanner />
    </Layout>
  );
};

export default ComparePage;
